import { Activity, TrendingUp, TrendingDown, AlertTriangle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/routes";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";

const heartRateData = [
  { time: "6AM", value: 68 },
  { time: "8AM", value: 72 },
  { time: "10AM", value: 85 },
  { time: "12PM", value: 78 },
  { time: "2PM", value: 74 },
  { time: "4PM", value: 82 },
  { time: "6PM", value: 76 },
  { time: "8PM", value: 70 },
];

interface PatientAlert {
  patientName: string;
  metric: string;
  value: string;
  status: "critical" | "warning" | "normal";
  change: "up" | "down";
}

const patientAlerts: PatientAlert[] = [
  {
    patientName: "Thomas Rivera",
    metric: "Blood Pressure",
    value: "158/98 mmHg",
    status: "critical",
    change: "up"
  },
  {
    patientName: "Nancy Kim",
    metric: "Heart Rate",
    value: "112 BPM",
    status: "warning",
    change: "up"
  },
  {
    patientName: "George Miller",
    metric: "Blood Glucose",
    value: "145 mg/dL",
    status: "warning",
    change: "down"
  },
];

const statusColors = {
  critical: "border-destructive/50 bg-destructive/5",
  warning: "border-warning/50 bg-warning/5",
  normal: "border-success/50 bg-success/5",
};

export function RemoteMonitoring() {
  const navigate = useNavigate();
  return (
    <div className="rounded-xl border border-border bg-card p-6 shadow-card animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate(ROUTES.PROVIDER_MONITORING)}>
          <div className="rounded-lg bg-destructive/10 p-2">
            <Activity className="h-5 w-5 text-destructive" />
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold">Remote Patient Monitoring</h3>
            <p className="text-sm text-muted-foreground">Real-time health alerts</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" className="gap-1" onClick={() => navigate(ROUTES.PROVIDER_MONITORING)}>
          All Patients <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Chart */}
      <div className="mb-6 rounded-lg bg-muted/30 p-4">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-medium text-foreground">Average Heart Rate Trend</p>
          <span className="flex items-center gap-1 text-sm text-success">
            <TrendingDown className="h-4 w-4" />
            -3% from yesterday
          </span>
        </div>
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={heartRateData}>
              <defs>
                <linearGradient id="heartRateGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.2}/>
                  <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis 
                dataKey="time" 
                axisLine={false} 
                tickLine={false}
                tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
              />
              <YAxis 
                domain={[60, 90]} 
                axisLine={false} 
                tickLine={false}
                tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                  boxShadow: 'var(--shadow-md)'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="value" 
                stroke="hsl(var(--destructive))" 
                strokeWidth={2}
                fill="url(#heartRateGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Alerts */}
      <div className="space-y-3">
        <p className="text-sm font-semibold text-foreground flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-warning" />
          Active Alerts ({patientAlerts.length})
        </p>
        {patientAlerts.map((alert, index) => (
          <div 
            key={alert.patientName}
            className={cn(
              "flex items-center justify-between rounded-lg border p-3 transition-all",
              statusColors[alert.status]
            )}
          >
            <div>
              <p className="font-medium text-foreground">{alert.patientName}</p>
              <p className="text-sm text-muted-foreground">{alert.metric}</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="font-semibold text-foreground">{alert.value}</p>
                <div className={cn(
                  "flex items-center gap-1 text-xs",
                  alert.change === "up" ? "text-destructive" : "text-success"
                )}>
                  {alert.change === "up" ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {alert.change === "up" ? "Increasing" : "Decreasing"}
                </div>
              </div>
              <Button size="sm" variant="outline">View</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
